import React, { useEffect, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const STATUS_BADGE = {
  draft:               { label: 'Utkast',              color: '#9ca3af', bg: 'rgba(156, 163, 175, 0.15)' },
  saved_to_mailchimp:  { label: 'Sparad i Mailchimp',  color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  sent:                { label: 'Skickad',             color: '#6366f1', bg: 'rgba(99, 102, 241, 0.15)' },
};

const MARKET_FLAGS = { SE: '🇸🇪', NO: '🇳🇴', FI: '🇫🇮', DK: '🇩🇰', DE: '🇩🇪', NL: '🇳🇱' };

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function NewsletterHistoryTab() {
  const { user } = useAuth();
  const [drafts, setDrafts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [emailById, setEmailById] = useState({});  // user_id → email (för "skapad av")

  const load = async () => {
    if (!user || !supabase) { setLoading(false); return; }
    setLoading(true);
    // Efter V22 är newsletter_drafts globalt läsbart för alla aktiva användare —
    // ingen .eq('user_id') filter.
    const { data, error } = await supabase
      .from('newsletter_drafts')
      .select('id,user_id,campaign_name,markets,generated_content,status,created_at')
      .order('created_at', { ascending: false })
      .limit(200);
    setLoading(false);
    if (error) {
      toast.error(`Kunde inte hämta historik: ${error.message}`);
      return;
    }
    const rows = data || [];
    setDrafts(rows);
    // Slå upp e-postadresser för "skapad av" via user_profiles (admin-RLS gör att alla aktiva kan läsa).
    const uniqueIds = [...new Set(rows.map(r => r.user_id).filter(Boolean))];
    if (uniqueIds.length > 0) {
      const { data: profs } = await supabase
        .from('user_profiles')
        .select('user_id,email')
        .in('user_id', uniqueIds);
      if (profs) setEmailById(profs.reduce((acc, p) => ({ ...acc, [p.user_id]: p.email }), {}));
    }
  };

  useEffect(() => { load(); }, [user?.id]);

  return (
    <div data-testid="nl-history-tab">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h2 className="dash-card-title" style={{ margin: 0 }}>Sparade utkast</h2>
        <button
          onClick={load}
          className="ap-queue-refresh"
          title="Uppdatera"
          data-testid="nl-history-refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', padding: 24 }}>
          <Loader2 size={14} className="ap-progress-spin" /> Laddar...
        </div>
      ) : drafts.length === 0 ? (
        <div className="admin-empty" data-testid="nl-history-empty">
          Inga sparade utkast ännu. Generera ett nyhetsbrev och spara det för att se det här.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {drafts.map(d => {
            const isOpen = expanded === d.id;
            const badge = STATUS_BADGE[d.status] || STATUS_BADGE.draft;
            return (
              <div
                key={d.id}
                className="dash-card"
                style={{ padding: 0, overflow: 'hidden' }}
                data-testid={`nl-draft-${d.id}`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : d.id)}
                  data-testid={`nl-draft-toggle-${d.id}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '24px 1.8fr 1.4fr 1fr 1fr',
                    alignItems: 'center', gap: 12,
                    width: '100%', padding: '14px 18px',
                    background: 'transparent', border: 'none',
                    color: 'var(--text)', textAlign: 'left', cursor: 'pointer',
                  }}
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                    {d.campaign_name || <em style={{ color: 'var(--muted)' }}>(namnlös)</em>}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }} title={emailById[d.user_id]}>
                    {emailById[d.user_id]
                      ? (d.user_id === user?.id ? 'Du' : emailById[d.user_id])
                      : '—'}
                  </span>
                  <span style={{ fontSize: 14 }}>
                    {(d.markets || []).map(m => <span key={m} style={{ marginRight: 4 }}>{MARKET_FLAGS[m] || m}</span>)}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                      fontSize: 11, fontWeight: 600, color: badge.color, background: badge.bg,
                    }}>
                      {badge.label}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{formatDate(d.created_at)}</span>
                  </span>
                </button>

                {isOpen && (
                  <div style={{ padding: '0 18px 18px', borderTop: '1px solid var(--border)' }}>
                    {(d.markets || []).map(mkt => {
                      const c = (d.generated_content || {})[mkt] || {};
                      return (
                        <div key={mkt} style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                            {MARKET_FLAGS[mkt] || mkt} — {mkt}
                          </div>
                          <div style={{ display: 'grid', gap: 6, fontSize: 12, color: 'var(--text)', lineHeight: 1.55 }}>
                            <Row label="Ämnesrad"  value={c.subject_line} />
                            <Row label="Preheader" value={c.preheader} />
                            <Row label="Hero"      value={c.hero} />
                            <Row label="Rubrik 1"  value={c.rubrik_1} />
                            <Row label="Text 1"    value={c.textblock_1} />
                            <Row label="CTA 1"     value={c.cta_1} />
                            {c.rubrik_2 && <Row label="Rubrik 2" value={c.rubrik_2} />}
                            {c.textblock_2 && <Row label="Text 2"   value={c.textblock_2} />}
                            {c.cta_2 && <Row label="CTA 2"    value={c.cta_2} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 10 }}>
      <span style={{ color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}
